import React, { useState } from "react";

import dayjs from "dayjs";

import { v4 as uuidv4 } from "uuid";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";

import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { GetTimezone } from "../../util";

import { useUserAuth } from "../../contexts/authContext";

import useGetRequest from "../../hooks/useGetRequest";

const ZeroConflictAnalysisCreator = ({ setReports, setCreatingReportType }) => {
    const { user, getOrganizationByID } = useUserAuth();

    const [selectedOperators, setSelectedOperators] = useState([]);
    const [selectedOperatorsErrorText, setSelectedOperatorsErrorText] = useState("");

    const currentTime = dayjs().set("second", 0).set("millisecond", 0);

    const [timeStart, setTimeStart] = useState(currentTime.subtract(1, "month"));
    const [timeEnd, setTimeEnd] = useState(currentTime);
    const [timeErrorText, setTimeErrorText] = useState("");

    const { data: operators, loading: operatorsLoading } = useGetRequest(`/api/analysis/zero_conflict/get_users/${user.id}`);

    const handleSubmit = () => {
        if (selectedOperators.length === 0) {
            return setSelectedOperatorsErrorText("Please select at least one user");
        } else if (selectedOperatorsErrorText) {
            setSelectedOperatorsErrorText("");
        }
        if (!timeStart || !timeStart.isValid() || !timeEnd || !timeEnd.isValid()) {
            return setTimeErrorText("Please provide a valid start and end time.");
        } else if (timeEnd.isBefore(timeStart) || timeEnd.diff(timeStart, "months") > 3) {
            return setTimeErrorText("Please ensure the end time is after the start, and the duration is no more than 3 months.");
        } else if (currentTime.diff(timeStart, "months") > 3) {
            return setTimeErrorText("Please select a start date within the past 3 months.");
        } else if (timeEnd.isAfter(currentTime)) {
            return setTimeErrorText("Please ensure both dates are in the past.");
        } else if (timeErrorText && timeErrorText.length) {
            setTimeErrorText("");
        }
        const body = selectedOperators.map((operator) => {
            return {
                request_uuid: uuidv4(),
                user_id: user.id,
                pilot_uuid: operator.pilot_uuid,
                organization_id: operator.organization_id,
                time_start: timeStart.toISOString(),
                time_end: timeEnd.toISOString()
            };
        });
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body)
        };
        fetch(`api/analysis/zero_conflict/submit`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((updatedReports) => {
                setReports(updatedReports);
                setCreatingReportType(null);
            })
            .catch((err) => console.error(err));
    };
    return (
        <Dialog onClose={() => setCreatingReportType(null)} open={true} maxWidth="xs" fullWidth>
            <DialogTitle>Request Zero Conflict Report?</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center", justifyContent: "center", pt: 1 }}>
                    <Autocomplete
                        multiple
                        fullWidth
                        options={operators || []}
                        getOptionLabel={(option) => option.email}
                        renderOption={(props, option) => {
                            const selected = selectedOperators.find((operator) => {
                                return operator.id === option.id;
                            });
                            const organization = getOrganizationByID(option.organization_id);
                            return (
                                <ListItemButton key={props.key} {...props} dense selected={selected}>
                                    <ListItemText primary={option.email} secondary={`${organization ? organization.name : "Unknown"} - ${option.user_role}`} />
                                </ListItemButton>
                            );
                        }}
                        loading={operatorsLoading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select users"
                                placeholder="Select users"
                                error={selectedOperatorsErrorText.length > 0 ? true : false}
                                helperText={selectedOperatorsErrorText}
                                fullWidth={true}
                            />
                        )}
                        value={selectedOperators}
                        onChange={(e, value) => setSelectedOperators(value)}
                        getOptionKey={(option) => option.id}
                        data-testid="autocomplete"
                    />
                    <Typography variant="caption" color="text.secondary" gutterBottom px={1}>
                        {"If you do not see an operator, please ensure that they have registered as a pilot."}
                    </Typography>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            label={`Start Time (${GetTimezone()}) *`}
                            value={timeStart}
                            onChange={(value) => setTimeStart(value)}
                            slotProps={{
                                textField: { fullWidth: true, error: timeErrorText.length > 0, inputProps: { "data-testid": "startTime" } }
                            }}
                        />
                        <DateTimePicker
                            label={`End Time (${GetTimezone()}) *`}
                            value={timeEnd}
                            onChange={(value) => setTimeEnd(value)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    error: timeErrorText.length > 0,
                                    helperText: timeErrorText,
                                    inputProps: { "data-testid": "endTime" }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setCreatingReportType(null)}>Close</Button>
                <Button onClick={handleSubmit}>Submit</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ZeroConflictAnalysisCreator;
